import { useGetClientIntegrationsQuery } from 'app/apis/queryApi';
import { RootState } from 'app/store';
import { ClientIntegrationDTO } from 'features/client-integrations/clientIntegration.model';
import IntegrationTile, {
  IntegrationStatus
} from 'features/client-integrations/integration-setup/integration-tile';
import TileGridContainer from 'features/tiles/tileGridContainer';
import TileWithoutDefaultStyling from 'features/tiles/tileWithoutDefaultStyling';
import { FC, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { selectAvailableIntegrations } from 'features/client-integrations/clientIntegration-meta-slice';

enum IntegrationSetupPage {
  GOOGLE_ADS = '/settings/integrations/setup/google-ads',
  FACEBOOK_ADS = '/settings/integrations/setup/facebook-ads'
}

export const AddIntegrationsPage: FC = () => {
  const navigate = useNavigate();

  const { siteId } = useSelector((state: RootState) => state.selectedSite);
  const { data: clientIntegrations = [] } = useGetClientIntegrationsQuery({
    siteId
  }) as {
    data: ClientIntegrationDTO[] | undefined;
  };

  const enabledDirectAPIIntegrations = useMemo(() => {
    return clientIntegrations
      .filter((ci) => ci.dataSource === 'DirectAPI')
      .map((ci) => ci.platform);
  }, [clientIntegrations]);

  const availableIntegrations = useSelector(
    selectAvailableIntegrations(enabledDirectAPIIntegrations)
  );

  const handleEnableClick = (
    integrationName: string,
    status: IntegrationStatus
  ) => {
    if (status === IntegrationStatus.NOT_CONFIGURED) {
      const setupPage =
        IntegrationSetupPage[
          integrationName
            .replace(/\s+/g, '_')
            .toUpperCase() as keyof typeof IntegrationSetupPage
        ];

      if (setupPage) {
        navigate(setupPage);
      }
    }
  };

  return (
    <TileGridContainer>
      <TileWithoutDefaultStyling className="bg-white border rounded border-neutral-200 p-2.5 shadow-sm w-full grow basis-full">
        <section className="mx-6 my-6">
          <div className="flex items-center justify-between border-b border-neutral-200 pb-4 mb-4 mt-2">
            <h2 className="text-primary-700 text-l">Add an Integration</h2>
          </div>
        </section>
        <section>
          <div className="flex flex-wrap gap-6 p-6 text-primary-700">
            {availableIntegrations.map((integration) => (
              <div
                key={integration.name}
                className="flex-1 min-w-[250px] max-w-[350px] flex flex-col justify-between h-full"
              >
                <IntegrationTile
                  name={integration.name}
                  status={integration.status}
                  logo={integration.logo}
                  onAction={() =>
                    handleEnableClick(integration.name, integration.status)
                  }
                />
              </div>
            ))}
          </div>
        </section>
      </TileWithoutDefaultStyling>
    </TileGridContainer>
  );
};
